/**
 * Types for Warehouse Map Editor
 */

export interface Position {
  x: number;
  y: number;
}

export interface Dimensions {
  width: number;
  height: number;
}

export interface ShelfSummary {
  level: number;
  heightCm: number;
  slotCount: number;
  totalItems: number;
  topProducts: { name: string; qty: number }[];
}

export interface MapElement {
  id: string;
  code?: string;
  name: string;
  type: ElementType;
  position: Position;
  dimensions: Dimensions;
  rotation: number;
  color: string;
  status?: ElementStatus;
  stockCount?: number;
  maxItems?: number | null;
  parentId?: string | null;
  isSelected?: boolean;
  isHovered?: boolean;
  confidence?: number;
  shelves?: ShelfSummary[];
}

export type ElementType =
  | 'ZONE'
  | 'AISLE'
  | 'RACK'
  | 'SHELF'
  | 'SLOT'
  | 'FLOOR'
  | 'COLD'
  | 'HAZMAT';

export type ElementStatus =
  | 'ACTIVE'
  | 'INACTIVE'
  | 'BLOCKED'
  | 'MAINTENANCE'
  | 'FULL';

export interface ElementTypeConfig {
  type: ElementType;
  label: string;
  icon: string;
  defaultColor: string;
  defaultWidth: number;
  defaultHeight: number;
  description: string;
}

export const ELEMENT_TYPES: ElementTypeConfig[] = [
  {
    type: 'ZONE',
    label: 'Strefa',
    icon: '🏷️',
    defaultColor: '#e8f5e9',
    defaultWidth: 200,
    defaultHeight: 150,
    description: 'Duża strefa magazynowa',
  },
  {
    type: 'RACK',
    label: 'Regał',
    icon: '📦',
    defaultColor: '#e3f2fd',
    defaultWidth: 80,
    defaultHeight: 40,
    description: 'Regał magazynowy',
  },
  {
    type: 'AISLE',
    label: 'Alejka',
    icon: '🛤️',
    defaultColor: '#fff3e0',
    defaultWidth: 200,
    defaultHeight: 30,
    description: 'Alejka między regałami',
  },
  {
    type: 'SHELF',
    label: 'Półka',
    icon: '📋',
    defaultColor: '#f3e5f5',
    defaultWidth: 60,
    defaultHeight: 30,
    description: 'Półka na regale',
  },
  {
    type: 'SLOT',
    label: 'Slot',
    icon: '📍',
    defaultColor: '#fce4ec',
    defaultWidth: 40,
    defaultHeight: 40,
    description: 'Pojedyncze miejsce',
  },
  {
    type: 'COLD',
    label: 'Chłodnia',
    icon: '❄️',
    defaultColor: '#e0f7fa',
    defaultWidth: 150,
    defaultHeight: 100,
    description: 'Strefa chłodnicza',
  },
  {
    type: 'HAZMAT',
    label: 'Mat. niebezp.',
    icon: '⚠️',
    defaultColor: '#ffebee',
    defaultWidth: 100,
    defaultHeight: 80,
    description: 'Materiały niebezpieczne',
  },
];

export interface ScanResult {
  success: boolean;
  mapDimensions: {
    width: number;
    height: number;
    originalWidth: number;
    originalHeight: number;
    scaleFactor: number;
  };
  detectedElements: DetectedElement[];
  elementsCount: number;
  previewImage: string;
  warehouse: {
    id: number;
    name: string;
  };
}

export interface DetectedElement {
  id: string;
  position: Position;
  dimensions: Dimensions;
  rotation: number;
  area: number;
  confidence: number;
  suggestedType: ElementType;
  suggestedColor: string;
  vertices: number;
  accepted?: boolean;
}

export interface EditorState {
  mode: EditorMode;
  selectedElement: MapElement | null;
  hoveredElement: MapElement | null;
  zoom: number;
  pan: Position;
  isDragging: boolean;
  isResizing: boolean;
  showGrid: boolean;
  gridSize: number;
  snapToGrid: boolean;
}

export type EditorMode =
  | 'select'
  | 'pan'
  | 'add'
  | 'delete'
  | 'set_start_point';

export interface ProductStock {
  id: number;
  productId: number;
  productName: string;
  quantity: number;
  reservedQuantity: number;
  batchNumber?: string;
}

